import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { GALLERY_ITEMS, BEFORE_AFTER } from "../data/content";

const CATEGORIES = ["All", "Roofing", "Siding", "Bathroom", "Kitchen"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");

  const items = useMemo(() => {
    if (filter === "All") return GALLERY_ITEMS;
    return GALLERY_ITEMS.filter((g) => g.category === filter);
  }, [filter]);

  return (
    <div data-testid="page-gallery">
      <section className="bg-[var(--navy)] text-white pt-36 pb-20 md:pb-28">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="eyebrow eyebrow-line mb-4">Gallery</div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-[5rem] leading-[1.02] tracking-tight">
              The Work,
              <br />
              <span className="italic font-light text-white/80">In Daylight.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              A selection of recent roof replacements, repairs and inspections
              across Braintree and the South Shore — plus a few interior projects
              for existing clients.
            </p>
          </div>
        </div>
      </section>

      {/* BEFORE & AFTER */}
      <section
        className="bg-white py-20 md:py-28 border-b border-[var(--border-light)]"
        data-testid="before-after-section"
      >
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow eyebrow-line mb-3">Before &amp; After</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] leading-[1.05] max-w-2xl">
                Same roof. Three days later.
              </h2>
            </div>
            <p className="text-[var(--text-mute)] text-sm md:text-base leading-relaxed max-w-md">
              Real Admiral Roofing projects — tear-off to fully installed
              architectural shingle, documented on the drone.
            </p>
          </div>

          <div className="space-y-16">
            {BEFORE_AFTER.map((ba) => (
              <article
                key={ba.id}
                data-testid={`before-after-${ba.id}`}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
              >
                <figure className="relative gallery-tile aspect-[4/3] group">
                  <img
                    src={ba.before.url}
                    alt={`${ba.title} — before`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <figcaption className="absolute top-4 left-4 bg-[var(--navy)] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5">
                    Before
                  </figcaption>
                  <div className="absolute bottom-4 left-4 right-4 text-white/90 text-xs tracking-[0.18em] uppercase">
                    {ba.before.caption}
                  </div>
                </figure>
                <figure className="relative gallery-tile aspect-[4/3] group">
                  <img
                    src={ba.after.url}
                    alt={`${ba.title} — after`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <figcaption className="absolute top-4 left-4 bg-[var(--copper)] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5">
                    After
                  </figcaption>
                  <div className="absolute bottom-4 left-4 right-4 text-white/90 text-xs tracking-[0.18em] uppercase">
                    {ba.after.caption}
                  </div>
                </figure>

                <div className="md:col-span-2 flex flex-col md:flex-row md:items-end justify-between gap-4 pt-2 border-t border-[var(--border-light)]">
                  <div>
                    <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--navy)] leading-tight">
                      {ba.title}
                    </h3>
                    <div className="text-xs uppercase tracking-[0.2em] text-[var(--copper)] mt-2">
                      {ba.location}
                    </div>
                  </div>
                  <p className="text-sm text-[var(--text-mute)] leading-relaxed max-w-lg">
                    {ba.summary}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="bg-white border-b border-[var(--border-light)] sticky top-20 z-30">
        <div className="container-x py-5 flex flex-wrap gap-3 md:gap-2 items-center">
          <span className="text-[10px] uppercase tracking-[0.24em] text-[var(--text-mute)] mr-2 hidden md:inline-flex items-center gap-2">
            <ArrowRight size={12} /> Filter
          </span>
          {CATEGORIES.map((c) => {
            const active = c === filter;
            return (
              <button
                key={c}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`text-xs uppercase tracking-[0.18em] font-bold px-4 py-2 transition-colors ${
                  active
                    ? "bg-[var(--navy)] text-white"
                    : "bg-transparent text-[var(--navy)] hover:bg-[var(--copper-light)]"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section className="bg-[var(--bg-light)] py-12 md:py-20">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {items.map((g, i) => (
              <article
                key={`${g.title}-${i}`}
                data-testid={`gallery-tile-${i}`}
                className={`gallery-tile aspect-[4/5] ${
                  i % 7 === 0 ? "md:row-span-2 md:aspect-[4/5.5]" : ""
                }`}
              >
                <img
                  src={g.url}
                  alt={g.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="tile-meta text-white">
                  <div className="eyebrow text-[var(--copper)] mb-1">{g.category}</div>
                  <h3 className="font-serif-display text-xl md:text-2xl leading-tight">
                    {g.title}
                  </h3>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/70 mt-2">
                    {g.location}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {items.length === 0 && (
            <div className="text-center py-20 text-[var(--text-mute)]" data-testid="gallery-empty">
              No projects to show under &ldquo;{filter}&rdquo; — check back soon.
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-20 md:py-28 border-t border-[var(--border-light)]">
        <div className="container-x text-center max-w-3xl mx-auto">
          <div className="eyebrow eyebrow-line mb-4">Your Roof Next</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] leading-[1.05] mb-10">
            Like what you see? We&rsquo;d love to add yours to the next page of this gallery.
          </h2>
          <Link to="/contact" data-testid="gallery-bottom-cta" className="btn-copper">
            Get a Free Estimate <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
